import React, { useState } from "react";
import { Spin, Select } from "antd";
import styled, { keyframes, css } from "styled-components";
import { useSelector } from "react-redux";
import { selectSettings } from "../../store/selector";

/* ═══════════════════════════════════════════════════════════════════════════
   DESIGN SYSTEM — "Precision Clarity"
   Bloomberg Terminal meets Apple Health. Deep confidence, crafted data.
   ═══════════════════════════════════════════════════════════════════════════ */

const C = {
  bg: "#F0F2F5",
  card: "#FFFFFF",
  cardBorder: "#E8ECF1",
  ink: "#0B1426",
  inkSoft: "#1E293B",
  inkMuted: "#475569",
  inkFaint: "#94A3B8",
  inkGhost: "#CBD5E1",
  accent: "#2563EB",
  accentSoft: "#DBEAFE",
  accentGlow: "rgba(37,99,235,0.08)",
  teal: "#0D9488",
  tealSoft: "#CCFBF1",
  tealGlow: "rgba(13,148,136,0.08)",
  emerald: "#059669",
  emeraldSoft: "#D1FAE5",
  emeraldGlow: "rgba(5,150,105,0.07)",
  amber: "#D97706",
  amberSoft: "#FEF3C7",
  amberGlow: "rgba(217,119,6,0.08)",
  rose: "#E11D48",
  roseSoft: "#FFE4E6",
  roseGlow: "rgba(225,29,72,0.06)",
  violet: "#7C3AED",
  violetSoft: "#EDE9FE",
  violetGlow: "rgba(124,58,237,0.07)",
  sky: "#0284C7",
  skySoft: "#E0F2FE",
  skyGlow: "rgba(2,132,199,0.08)",
  divider: "#E8ECF1",
  frost: "rgba(255,255,255,0.7)",
  shadow1: "0 1px 2px rgba(11,20,38,0.03), 0 2px 8px rgba(11,20,38,0.04)",
  shadow2: "0 4px 16px rgba(11,20,38,0.06), 0 1px 3px rgba(11,20,38,0.04)",
  shadow3: "0 8px 32px rgba(11,20,38,0.08), 0 2px 6px rgba(11,20,38,0.03)"
};

const PAL = [C.accent, C.teal, C.emerald, C.violet, C.amber, C.rose, C.sky];

/* ── Animations ──────────────────────────────────────────────────────────── */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;
/* ── Global Styles ───────────────────────────────────────────────────────── */
const Page = styled.div`
  width: 100%;
  max-width: 1520px;
  margin: 0 auto;
  padding: 28px 32px 56px;
  min-height: calc(100vh - 80px);
  overflow-y: auto;
  background: ${C.bg};
  font-family: "DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
  color: ${C.ink};

  @import url("https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&family=DM+Mono:wght@400;500&display=swap");
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$cols || "1fr"};
  gap: ${(p) => p.$gap || 20}px;
  margin-bottom: ${(p) => p.$mb || 28}px;
  @media (max-width: 1360px) {
    grid-template-columns: ${(p) => p.$md || p.$cols || "1fr"};
  }
  @media (max-width: 900px) {
    grid-template-columns: ${(p) => p.$sm || "1fr"};
  }
`;

const Card = styled.div`
  background: ${C.card};
  border: 1px solid ${C.cardBorder};
  border-radius: 16px;
  padding: ${(p) => p.$p || "28px"};
  box-shadow: ${C.shadow1};
  transition: box-shadow 0.3s ease, transform 0.3s ease;
  animation: ${fadeIn} 0.5s ease both;
  animation-delay: ${(p) => (p.$d || 0) * 55}ms;
  position: relative;
  overflow: hidden;

  &:hover {
    box-shadow: ${C.shadow2};
    transform: translateY(-1px);
  }
  ${(p) =>
    p.$span &&
    css`
      grid-column: span ${p.$span};
    `}
`;

/* ── Typography ──────────────────────────────────────────────────────────── */
const Heading = styled.h3`
  font-size: ${(p) => p.$size || "14px"};
  font-weight: 600;
  color: ${(p) => p.$color || C.inkSoft};
  margin: 0 0 ${(p) => p.$mb || "0"};
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const Label = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${C.inkFaint};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const Mono = styled.span`
  font-family: "DM Mono", "SF Mono", "Fira Code", monospace;
  font-size: ${(p) => p.$size || "28px"};
  font-weight: ${(p) => p.$weight || "500"};
  color: ${(p) => p.$color || C.ink};
  letter-spacing: -0.02em;
  line-height: 1;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 100px;
  color: ${(p) => p.$color || C.emerald};
  background: ${(p) => p.$bg || C.emeraldGlow};
  white-space: nowrap;
`;

const Tag = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${C.inkFaint};
  background: ${C.bg};
  border: 1px solid ${C.divider};
  padding: 4px 12px;
  border-radius: 100px;
  letter-spacing: 0.02em;
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${C.inkMuted};
  font-weight: 500;
  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 2px;
    background: ${(p) => p.$c};
  }
`;

/* ── KPI Card ────────────────────────────────────────────────────────────── */
const KPICard = styled(Card)`
  padding: 22px 24px;
  border-top: 2.5px solid ${(p) => p.$accent || C.accent};

  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    width: 120px;
    height: 120px;
    background: radial-gradient(circle at 100% 0%, ${(p) => p.$accent || C.accent}08 0%, transparent 70%);
    pointer-events: none;
  }
`;

const KPIIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${(p) => p.$bg || C.accentGlow};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    inset: -1px;
    border-radius: 13px;
    border: 1px solid ${(p) => p.$accent || C.accent}18;
  }
`;

/* ═══════════════════════════════════════════════════════════════════════════
   SVG CHART PRIMITIVES — Refined rendering with better proportions
   ═══════════════════════════════════════════════════════════════════════════ */

const smooth = (pts) => {
  if (pts.length < 2) return "";
  let d = `M ${pts[0][0]},${pts[0][1]}`;
  for (let i = 1; i < pts.length; i++) {
    const [x0, y0] = pts[i - 1];
    const [x1, y1] = pts[i];
    const cpx = (x0 + x1) / 2;
    d += ` C ${cpx},${y0} ${cpx},${y1} ${x1},${y1}`;
  }
  return d;
};

/* Sparkline */
const Spark = ({ data, color, w = 72, h = 22 }) => {
  if (!data || data.length < 2) return null;
  const mn = Math.min(...data),
    mx = Math.max(...data),
    rng = mx - mn || 1;
  const pts = data.map((v, i) => [(i / (data.length - 1)) * w, h - ((v - mn) / rng) * h * 0.75 - h * 0.12]);
  return (
    <svg width={w} height={h} style={{ display: "block" }}>
      <defs>
        <linearGradient id={`spk${color.replace(/#/g, "")}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.22} />
          <stop offset="100%" stopColor={color} stopOpacity={0.01} />
        </linearGradient>
      </defs>
      <path d={smooth(pts) + ` L ${w},${h} L 0,${h} Z`} fill={`url(#spk${color.replace(/#/g, "")})`} />
      <path d={smooth(pts)} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" />
    </svg>
  );
};

/* Line / Area Chart */
const LineChart = ({ sets, labels, h = 180, colors: lc, area = true, dots = true }) => {
  const all = sets.flat();
  const mx = Math.max(...all) * 1.06,
    mn = Math.min(...all) * 0.94,
    rng = mx - mn || 1;
  const pl = 36,
    w = Math.max(labels.length * 62, 400);
  const toY = (v) => h - ((v - mn) / rng) * (h - 16);
  const toX = (i) => pl + i * ((w - pl * 2) / (labels.length - 1));

  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h + 32}`} style={{ display: "block" }} preserveAspectRatio="xMidYMid meet">
      <defs>
        {sets.map((_, di) => (
          <linearGradient key={di} id={`lg${di}${(lc?.[di] || PAL[di]).replace(/#/g, "")}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={lc?.[di] || PAL[di]} stopOpacity={0.15} />
            <stop offset="100%" stopColor={lc?.[di] || PAL[di]} stopOpacity={0.01} />
          </linearGradient>
        ))}
      </defs>
      {[0.25, 0.5, 0.75].map((p) => (
        <line key={p} x1={pl} y1={h * (1 - p)} x2={w - pl} y2={h * (1 - p)} stroke={C.divider} strokeWidth={0.6} />
      ))}
      {[0, 0.5, 1].map((p) => (
        <text
          key={p}
          x={pl - 8}
          y={h - p * (h - 16) + 4}
          textAnchor="end"
          fontSize={10}
          fill={C.inkGhost}
          fontFamily="DM Mono, monospace"
        >
          {Math.round(mn + rng * p)}
        </text>
      ))}
      {sets.map((data, di) => {
        const pts = data.map((v, i) => [toX(i), toY(v)]);
        const line = smooth(pts);
        const col = lc?.[di] || PAL[di];
        return (
          <React.Fragment key={di}>
            {area && (
              <path
                d={line + ` L ${pts[pts.length - 1][0]},${h} L ${pts[0][0]},${h} Z`}
                fill={`url(#lg${di}${col.replace(/#/g, "")})`}
              />
            )}
            <path d={line} fill="none" stroke={col} strokeWidth={2} strokeLinecap="round" />
            {dots &&
              pts.map(([x, y], i) => (
                <g key={i}>
                  <circle cx={x} cy={y} r={3.5} fill={C.card} stroke={col} strokeWidth={1.8} />
                  <title>{`${labels[i]}: ${data[i]}`}</title>
                </g>
              ))}
          </React.Fragment>
        );
      })}
      {labels.map((l, i) => (
        <text
          key={i}
          x={toX(i)}
          y={h + 20}
          textAnchor="middle"
          fontSize={10.5}
          fill={C.inkGhost}
          fontFamily="DM Sans, sans-serif"
          fontWeight={500}
        >
          {l}
        </text>
      ))}
    </svg>
  );
};

/* Vertical Bar */
const BarChart = ({ data, labels, color = C.accent, h = 150, horizontal = false }) => {
  const mx = Math.max(...data);
  if (horizontal) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {data.map((v, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span
              style={{ fontSize: 12, color: C.inkMuted, width: 72, textAlign: "right", flexShrink: 0, fontWeight: 500 }}
            >
              {labels[i]}
            </span>
            <div style={{ flex: 1, height: 24, background: C.bg, borderRadius: 6, overflow: "hidden" }}>
              <div
                style={{
                  width: `${(v / mx) * 100}%`,
                  height: "100%",
                  borderRadius: 6,
                  background: `linear-gradient(90deg, ${color}, ${color}cc)`,
                  transition: "width 0.6s ease"
                }}
              />
            </div>
            <span
              style={{ fontSize: 12, fontWeight: 600, color: C.inkSoft, width: 48, fontFamily: "DM Mono, monospace" }}
            >
              {v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}
            </span>
          </div>
        ))}
      </div>
    );
  }
  const gap = 5,
    barW = Math.max(16, Math.min(28, 360 / data.length - gap));
  const w = data.length * (barW + gap) + 20;
  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h + 26}`} style={{ display: "block" }} preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id={`bGrd${color.replace(/#/g, "")}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={1} />
          <stop offset="100%" stopColor={color} stopOpacity={0.55} />
        </linearGradient>
      </defs>
      {data.map((v, i) => {
        const barH = (v / mx) * (h - 6);
        const x = 10 + i * (barW + gap);
        return (
          <g key={i}>
            <rect
              x={x}
              y={h - barH}
              width={barW}
              height={barH}
              rx={4}
              fill={`url(#bGrd${color.replace(/#/g, "")})`}
              opacity={0.9}
            >
              <title>{`${labels?.[i] || i}: ${v.toLocaleString()}`}</title>
            </rect>
            {labels?.[i] && (
              <text x={x + barW / 2} y={h + 16} textAnchor="middle" fontSize={9.5} fill={C.inkGhost} fontWeight={500}>
                {labels[i]}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

/* Stacked Bar */
const StackedBar = ({ data, labels, colors: bc, h = 150, legendLabels }) => {
  const totals = data[0].map((_, i) => data.reduce((s, row) => s + row[i], 0));
  const mx = Math.max(...totals);
  const gap = 4,
    barW = Math.max(16, Math.min(26, 360 / data[0].length - gap));
  const w = data[0].length * (barW + gap) + 20;
  return (
    <div>
      <svg width="100%" viewBox={`0 0 ${w} ${h + 26}`} style={{ display: "block" }} preserveAspectRatio="xMidYMid meet">
        {data[0].map((_, i) => {
          let y = h;
          const x = 10 + i * (barW + gap);
          return (
            <g key={i}>
              {data.map((row, di) => {
                const barH = (row[i] / mx) * (h - 6);
                y -= barH;
                return (
                  <rect
                    key={di}
                    x={x}
                    y={y}
                    width={barW}
                    height={barH}
                    rx={di === data.length - 1 ? 3 : 0}
                    fill={bc[di]}
                    opacity={0.85}
                  >
                    <title>{`${legendLabels[di]}: ${row[i]}`}</title>
                  </rect>
                );
              })}
              {i % 2 === 0 && labels?.[i] && (
                <text x={x + barW / 2} y={h + 16} textAnchor="middle" fontSize={9.5} fill={C.inkGhost}>
                  {labels[i]}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <div style={{ display: "flex", gap: 16, marginTop: 12, flexWrap: "wrap" }}>
        {legendLabels.map((l, i) => (
          <LegendItem key={i} $c={bc[i]}>
            {l}
          </LegendItem>
        ))}
      </div>
    </div>
  );
};

/* Donut */
const Donut = ({ segments, size = 160, centerLabel }) => {
  const total = segments.reduce((s, x) => s + x.value, 0);
  let cum = 0;
  const r = (size - 24) / 2,
    circ = 2 * Math.PI * r;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={C.bg} strokeWidth={18} />
        {segments.map((seg, i) => {
          const pct = seg.value / total,
            off = cum * circ;
          cum += pct;
          return (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={seg.color}
              strokeWidth={18}
              strokeDasharray={`${pct * circ} ${circ}`}
              strokeDashoffset={-off}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
              strokeLinecap="butt"
              style={{ transition: "stroke-dasharray 0.7s ease" }}
            >
              <title>{`${seg.label}: ${seg.value}`}</title>
            </circle>
          );
        })}
        <text
          x={size / 2}
          y={size / 2 - 6}
          textAnchor="middle"
          fontSize={22}
          fontWeight={600}
          fill={C.ink}
          fontFamily="DM Mono, monospace"
          letterSpacing={-0.5}
        >
          {total.toLocaleString()}
        </text>
        <text x={size / 2} y={size / 2 + 12} textAnchor="middle" fontSize={10} fill={C.inkFaint} fontWeight={500}>
          {centerLabel || "total"}
        </text>
      </svg>
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        {segments.map((seg, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: seg.color, flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: C.inkMuted, flex: 1 }}>{seg.label}</span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: C.inkSoft, fontFamily: "DM Mono, monospace" }}>
              {seg.value.toLocaleString()}
            </span>
            <span style={{ fontSize: 10.5, color: C.inkGhost, width: 28, textAlign: "right" }}>
              {((seg.value / total) * 100).toFixed(0)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* Gauge */
const Gauge = ({ value, max = 100, label, color = C.accent, size = 110 }) => {
  const pct = Math.min(value / max, 1);
  const r = (size - 14) / 2,
    circ = 2 * Math.PI * r;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={C.bg} strokeWidth={7} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={7}
          strokeLinecap="round"
          strokeDasharray={`${pct * circ} ${circ}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dasharray 0.8s ease" }}
        />
        <text
          x={size / 2}
          y={size / 2 + 1}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={20}
          fontWeight={600}
          fill={C.ink}
          fontFamily="DM Mono, monospace"
        >
          {value}%
        </text>
      </svg>
      {label && <span style={{ fontSize: 12, fontWeight: 500, color: C.inkMuted, textAlign: "center" }}>{label}</span>}
    </div>
  );
};

/* Funnel */
const Funnel = ({ steps, values }) => {
  const max = values[0] || 1;
  const colors = ["#2563EB", "#3B82F6", "#60A5FA", "#93C5FD", "#BFDBFE", "#DBEAFE"];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {steps.map((step, i) => {
        const pct = (values[i] / max) * 100;
        const conv = i > 0 ? ((values[i] / values[i - 1]) * 100).toFixed(0) : null;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <span
              style={{ fontSize: 12, color: C.inkMuted, fontWeight: 500, width: 96, textAlign: "right", flexShrink: 0 }}
            >
              {step}
            </span>
            <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
              <div
                style={{
                  width: `${30 + pct * 0.7}%`,
                  height: 34,
                  borderRadius: 7,
                  background: colors[i],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: `0 2px 8px ${colors[i]}33`,
                  transition: "width 0.6s cubic-bezier(.4,0,.2,1)"
                }}
              >
                <span
                  style={{
                    fontSize: 13.5,
                    fontWeight: 700,
                    color: "#fff",
                    fontFamily: "DM Mono, monospace",
                    letterSpacing: -0.3
                  }}
                >
                  {values[i].toLocaleString()}
                </span>
              </div>
            </div>
            <div style={{ width: 52, flexShrink: 0, textAlign: "center" }}>
              {conv && (
                <Badge
                  $color={parseInt(conv) >= 85 ? C.emerald : parseInt(conv) >= 70 ? C.amber : C.rose}
                  $bg={parseInt(conv) >= 85 ? C.emeraldGlow : parseInt(conv) >= 70 ? C.amberGlow : C.roseGlow}
                >
                  {conv}%
                </Badge>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* Survival */
const Survival = ({ data, labels, h = 160 }) => {
  const pl = 36,
    w = Math.max(data.length * 56, 400);
  const toY = (v) => h - (v / 100) * (h - 16) - 8;
  const toX = (i) => pl + i * ((w - pl * 2) / (data.length - 1));
  const pts = data.map((v, i) => [toX(i), toY(v)]);
  const line = smooth(pts);
  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h + 32}`} style={{ display: "block" }} preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="survG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={C.rose} stopOpacity={0.1} />
          <stop offset="100%" stopColor={C.rose} stopOpacity={0} />
        </linearGradient>
      </defs>
      {[25, 50, 75].map((v) => (
        <line key={v} x1={pl} y1={toY(v)} x2={w - pl} y2={toY(v)} stroke={C.divider} strokeWidth={0.5} />
      ))}
      {[0, 50, 100].map((v) => (
        <text
          key={v}
          x={pl - 8}
          y={toY(v) + 4}
          textAnchor="end"
          fontSize={10}
          fill={C.inkGhost}
          fontFamily="DM Mono, monospace"
        >
          {v}%
        </text>
      ))}
      <path d={line + ` L ${pts[pts.length - 1][0]},${h} L ${pts[0][0]},${h} Z`} fill="url(#survG)" />
      <path d={line} fill="none" stroke={C.rose} strokeWidth={2} strokeLinecap="round" />
      {pts.map(([x, y], i) => (
        <g key={i}>
          <circle cx={x} cy={y} r={3} fill={C.card} stroke={C.rose} strokeWidth={1.8} />
          <title>{`${labels[i]}: ${data[i]}%`}</title>
        </g>
      ))}
      {labels.map((l, i) => (
        <text key={i} x={toX(i)} y={h + 20} textAnchor="middle" fontSize={10} fill={C.inkGhost} fontWeight={500}>
          {l}
        </text>
      ))}
    </svg>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   DATA
   ═══════════════════════════════════════════════════════════════════════════ */

// Realistic data for Ocrevus (MS, ~96K US patients on therapy, infusion every 6mo)
// and Xolair (allergic asthma, ~150K US patients, injection every 2-4 weeks)
// Combined program covering both brands through Genentech hub
const DEFAULT_DATA = {
  kpis: {
    totalPatients: { value: 3842, delta: "+8%", sparkline: [3100, 3220, 3340, 3480, 3580, 3710, 3842] },
    activePatients: { value: 2918, delta: "+6%", sparkline: [2380, 2490, 2580, 2650, 2740, 2830, 2918] },
    enrollmentRate: { value: 71.3, delta: "+3.1%", sparkline: [62, 64, 65, 67, 68, 70, 71.3] },
    copayUtilization: { value: 58.7, delta: "+4.2%", sparkline: [48, 50, 52, 54, 56, 57, 58.7] },
    daysOnTherapy: { value: 312, delta: "+22d", sparkline: [245, 258, 272, 285, 295, 304, 312] },
    activeMAU: { value: 1247, delta: "+11%", sparkline: [890, 940, 1010, 1060, 1120, 1180, 1247] }
  },
  funnel: {
    steps: ["Eligible", "Signed Up", "Active", "Adherent"],
    values: [3842, 2740, 2918, 1986]
  },
  access: {
    paApprovalTrend: {
      labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
      data: [68.5, 69.8, 71.2, 72.0, 73.4, 74.1, 74.8, 75.6, 76.2, 76.8, 77.3, 77.9]
    },
    timeToPA: {
      labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
      data: [22, 21, 20.5, 19.8, 19.2, 18.5, 18.0, 17.4, 17.0, 16.5, 16.2, 15.8]
    },
    coverageMix: [
      { label: "Commercial", value: 1845, color: C.accent },
      { label: "Medicare", value: 1082, color: C.sky },
      { label: "Medicaid", value: 612, color: C.teal },
      { label: "Uninsured", value: 303, color: C.amber }
    ]
  },
  affordability: {
    assistanceMix: {
      labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
      copay: [142, 148, 155, 162, 170, 178, 185, 192, 198, 205, 212, 218],
      pap: [58, 62, 65, 68, 72, 75, 78, 82, 85, 88, 92, 95],
      none: [85, 82, 78, 74, 70, 66, 62, 58, 55, 52, 48, 45]
    },
    timeToAssistance: {
      labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
      data: [14.2, 13.5, 12.8, 12.1, 11.6, 11.0]
    },
    copayEnrollRate: 58.7
  },
  adherence: {
    dotTrend: {
      labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
      data: [245, 258, 272, 285, 295, 304, 312, 318, 325, 330, 336, 342]
    },
    cyclesDistribution: {
      labels: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"],
      data: [384, 362, 341, 318, 298, 275, 258, 240, 225, 210, 198, 185]
    },
    survivalCurve: {
      labels: ["M0", "M1", "M2", "M3", "M4", "M5", "M6", "M9", "M12", "M18", "M24"],
      data: [100, 96, 92, 89, 86, 83, 80, 74, 68, 61, 55]
    }
  },
  engagement: {
    dauMau: {
      labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
      dau: [185, 198, 212, 225, 238, 252, 268, 280, 295, 308, 320, 335],
      mau: [890, 940, 1010, 1060, 1120, 1180, 1247, 1290, 1340, 1380, 1420, 1460]
    },
    engagementRate: 32.4,
    contentPerformance: {
      labels: ["Education", "Reminders", "Financial", "Support", "Wellness"],
      data: [2480, 1850, 1540, 1180, 820]
    }
  },
  insights: [
    {
      type: "warning",
      text: "Ocrevus infusion adherence drops 12% after month 6 — consider proactive outreach before 2nd dose"
    },
    { type: "success", text: "Xolair patients using copay cards show 2.1x higher 12-month persistence vs unassisted" },
    { type: "danger", text: "18% of Medicare patients abandon Ocrevus after PA denial — appeal support underutilized" },
    {
      type: "info",
      text: "Xolair self-injection patients engage with education content 34% more than infusion patients"
    },
    { type: "success", text: "Transportation assistance requests up 28% — MS Focus grant utilization improving" },
    { type: "warning", text: "Average time-to-first-fill for new Ocrevus patients is 38 days — above 30-day target" }
  ]
};

/* ═══════════════════════════════════════════════════════════════════════════
   DASHBOARD
   ═══════════════════════════════════════════════════════════════════════════ */

const AnalyticsDashboard = () => {
  const settings = useSelector(selectSettings);
  const [period, setPeriod] = useState("ytd");
  const [brand, setBrand] = useState("all");
  const [network, setNetwork] = useState("all");
  const [segment, setSegment] = useState("all");

  const data = (() => {
    try {
      const raw = settings?.["insights.analytics-dashboard-data"];
      if (!raw) return DEFAULT_DATA;
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      return { ...DEFAULT_DATA, ...parsed };
    } catch {
      return DEFAULT_DATA;
    }
  })();

  if (!settings)
    return (
      <Page style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
        <Spin size="large" />
      </Page>
    );

  const {
    kpis: rawKpis,
    funnel: rawFunnel,
    access: rawAccess,
    affordability: rawAffordability,
    adherence: rawAdherence,
    engagement: rawEngagement,
    insights
  } = data;

  // ── Filter multipliers — simulate filtered data views ─────────────────
  const periodMult = { "30d": 0.12, "90d": 0.35, ytd: 1 };
  const brandMult = { all: 1, ocrevus: 0.55, xolair: 0.45 };
  const networkMult = { all: 1, hs: 0.42, sp: 0.28, onc: 0.18, inf: 0.12 };
  const segmentMult = { all: 1, new: 0.24, active: 0.76 };

  // Combined multiplier for count-based metrics
  const countMult = (brandMult[brand] || 1) * (networkMult[network] || 1) * (segmentMult[segment] || 1);
  // Period affects time-series data (take last N% of array), count multiplier scales values
  const periodFactor = periodMult[period] || 1;

  // Helpers to apply multipliers
  const scaleVal = (v) => Math.round(v * countMult);
  const scaleArr = (arr) => arr.map((v) => Math.round(v * countMult));
  const sliceArr = (arr) => {
    if (period === "30d") return arr.slice(-1);
    if (period === "90d") return arr.slice(-3);
    return arr;
  };
  const scaleAndSlice = (arr) => sliceArr(arr).map((v) => Math.round(v * countMult));
  const sliceLabels = (labels) => {
    if (period === "30d") return labels.slice(-1);
    if (period === "90d") return labels.slice(-3);
    return labels;
  };
  // Rate-based metrics get a slight variance instead of raw multiplication
  const varyRate = (v) => {
    const offset = (countMult - 1) * 5 + (periodFactor - 1) * 3;
    return Math.round((v + offset) * 10) / 10;
  };
  const varyRateArr = (arr) => arr.map((v) => varyRate(v));

  // Apply filters to all data
  const kpis = {
    totalPatients: {
      ...rawKpis.totalPatients,
      value: scaleVal(rawKpis.totalPatients.value),
      sparkline: scaleArr(rawKpis.totalPatients.sparkline)
    },
    activePatients: {
      ...rawKpis.activePatients,
      value: scaleVal(rawKpis.activePatients.value),
      sparkline: scaleArr(rawKpis.activePatients.sparkline)
    },
    enrollmentRate: {
      ...rawKpis.enrollmentRate,
      value: varyRate(rawKpis.enrollmentRate.value),
      sparkline: varyRateArr(rawKpis.enrollmentRate.sparkline)
    },
    copayUtilization: {
      ...rawKpis.copayUtilization,
      value: varyRate(rawKpis.copayUtilization.value),
      sparkline: varyRateArr(rawKpis.copayUtilization.sparkline)
    },
    daysOnTherapy: {
      ...rawKpis.daysOnTherapy,
      value: Math.round(rawKpis.daysOnTherapy.value * (0.7 + countMult * 0.3)),
      sparkline: rawKpis.daysOnTherapy.sparkline.map((v) => Math.round(v * (0.7 + countMult * 0.3)))
    },
    activeMAU: {
      ...rawKpis.activeMAU,
      value: scaleVal(rawKpis.activeMAU.value),
      sparkline: scaleArr(rawKpis.activeMAU.sparkline)
    }
  };
  const funnel = { steps: rawFunnel.steps, values: rawFunnel.values.map((v) => scaleVal(v)) };
  const fLabels = sliceLabels(rawAccess.paApprovalTrend.labels);
  const access = {
    paApprovalTrend: { labels: fLabels, data: sliceArr(varyRateArr(rawAccess.paApprovalTrend.data)) },
    timeToPA: { labels: fLabels, data: sliceArr(rawAccess.timeToPA.data) },
    coverageMix: rawAccess.coverageMix.map((seg) => ({ ...seg, value: scaleVal(seg.value) }))
  };
  const aLabels = sliceLabels(rawAffordability.assistanceMix.labels);
  const affordability = {
    assistanceMix: {
      labels: aLabels,
      copay: scaleAndSlice(rawAffordability.assistanceMix.copay),
      pap: scaleAndSlice(rawAffordability.assistanceMix.pap),
      none: scaleAndSlice(rawAffordability.assistanceMix.none)
    },
    timeToAssistance: {
      labels: sliceLabels(rawAffordability.timeToAssistance.labels),
      data: sliceArr(rawAffordability.timeToAssistance.data)
    },
    copayEnrollRate: varyRate(rawAffordability.copayEnrollRate)
  };
  const adLabels = sliceLabels(rawAdherence.dotTrend.labels);
  const adherence = {
    dotTrend: {
      labels: adLabels,
      data: sliceArr(rawAdherence.dotTrend.data.map((v) => Math.round(v * (0.7 + countMult * 0.3))))
    },
    cyclesDistribution: {
      labels: rawAdherence.cyclesDistribution.labels,
      data: scaleArr(rawAdherence.cyclesDistribution.data)
    },
    survivalCurve: {
      labels: rawAdherence.survivalCurve.labels,
      data: rawAdherence.survivalCurve.data.map((v) => varyRate(v))
    }
  };
  const eLabels = sliceLabels(rawEngagement.dauMau.labels);
  const engagement = {
    dauMau: {
      labels: eLabels,
      dau: scaleAndSlice(rawEngagement.dauMau.dau),
      mau: scaleAndSlice(rawEngagement.dauMau.mau)
    },
    engagementRate: varyRate(rawEngagement.engagementRate),
    contentPerformance: {
      labels: rawEngagement.contentPerformance.labels,
      data: scaleArr(rawEngagement.contentPerformance.data)
    }
  };

  const kpiDefs = [
    {
      key: "totalPatients",
      label: "Total Patients",
      fmt: (v) => v.toLocaleString(),
      accent: C.accent,
      bg: C.accentGlow,
      ico: "👥"
    },
    {
      key: "activePatients",
      label: "Active Patients",
      fmt: (v) => v.toLocaleString(),
      accent: C.teal,
      bg: C.tealGlow,
      ico: "📈"
    },
    {
      key: "activeMAU",
      label: "Monthly Active Users",
      fmt: (v) => v.toLocaleString(),
      accent: C.sky,
      bg: C.skyGlow,
      ico: "📱"
    },
    {
      key: "enrollmentRate",
      label: "Enrollment Rate",
      fmt: (v) => `${v}%`,
      accent: C.emerald,
      bg: C.emeraldGlow,
      ico: "📋"
    },
    {
      key: "copayUtilization",
      label: "Copay Utilization",
      fmt: (v) => `${v}%`,
      accent: C.violet,
      bg: C.violetGlow,
      ico: "💳"
    },
    {
      key: "daysOnTherapy",
      label: "Days on Therapy",
      fmt: (v) => v.toString(),
      accent: C.amber,
      bg: C.amberGlow,
      ico: "📅"
    }
  ];

  const insStyles = {
    warning: { bg: C.amberSoft, bdr: C.amber, ico: "⚠" },
    success: { bg: C.emeraldSoft, bdr: C.emerald, ico: "↗" },
    danger: { bg: C.roseSoft, bdr: C.rose, ico: "!" },
    info: { bg: C.accentSoft, bdr: C.accent, ico: "i" }
  };

  return (
    <Page>
      {/* ── Google Fonts injection ──────────────────────────────────── */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&family=DM+Mono:wght@400;500&display=swap');`}</style>

      {/* ── Header ──────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginBottom: 32,
          flexWrap: "wrap",
          gap: 16
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 28,
              fontWeight: 700,
              color: C.ink,
              margin: 0,
              letterSpacing: "-0.03em",
              lineHeight: 1.2
            }}
          >
            Insights
          </h1>
          <p style={{ fontSize: 13.5, color: C.rose, margin: "6px 0 0", fontWeight: 500, letterSpacing: "0.01em" }}>
            Mock data — Only for illustration
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Select
            value={period}
            onChange={setPeriod}
            size="small"
            style={{ width: 125 }}
            options={[
              { value: "30d", label: "Last 30 Days" },
              { value: "90d", label: "Last 90 Days" },
              { value: "ytd", label: "Year to Date" }
            ]}
          />
          <Select
            value={brand}
            onChange={setBrand}
            size="small"
            style={{ width: 115 }}
            options={[
              { value: "all", label: "All Brands" },
              { value: "ocrevus", label: "Ocrevus" },
              { value: "xolair", label: "Xolair" }
            ]}
          />
          <Select
            value={network}
            onChange={setNetwork}
            size="small"
            style={{ width: 120 }}
            options={[
              { value: "all", label: "All Networks" },
              { value: "hs", label: "Health System" },
              { value: "sp", label: "Specialty Rx" },
              { value: "onc", label: "Community Oncology" },
              { value: "inf", label: "Infusion Centers" }
            ]}
          />
          <Select
            value={segment}
            onChange={setSegment}
            size="small"
            style={{ width: 115 }}
            options={[
              { value: "all", label: "All Patients" },
              { value: "new", label: "New" },
              { value: "active", label: "Active" }
            ]}
          />
          <Tag>Updated {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" })}</Tag>
        </div>
      </div>

      {/* ── KPI Row ─────────────────────────────────────────────────── */}
      <Grid $cols="repeat(6, 1fr)" $md="repeat(3, 1fr)" $sm="repeat(2, 1fr)" $gap={16} $mb={28}>
        {kpiDefs.map((k, i) => {
          const d = kpis[k.key];
          return (
            <KPICard key={i} $d={i} $accent={k.accent} $p="22px 24px">
              <KPIIconWrap $bg={k.bg} $accent={k.accent}>
                <span style={{ fontSize: 18 }}>{k.ico}</span>
              </KPIIconWrap>
              <Label>{k.label}</Label>
              <Mono $size="32px" $weight="600" style={{ display: "block", margin: "8px 0 6px" }}>
                {k.fmt(d.value)}
              </Mono>
              <Spark data={d.sparkline} color={k.accent} />
              <div style={{ marginTop: 10 }}>
                <Badge
                  $color={d.delta?.startsWith("-") ? C.rose : C.emerald}
                  $bg={d.delta?.startsWith("-") ? C.roseGlow : C.emeraldGlow}
                >
                  {d.delta} <span style={{ fontWeight: 400, opacity: 0.7 }}>vs prev</span>
                </Badge>
              </div>
            </KPICard>
          );
        })}
      </Grid>

      {/* ── Funnel + Insights ──────────────────────────────────────── */}
      <Grid $cols="1fr 320px" $md="1fr" $mb={28}>
        <Card $d={7} $p="28px">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <Heading $size="16px">Patient Journey Funnel</Heading>
            <Tag>Eligible → Adherent</Tag>
          </div>
          <Funnel steps={funnel.steps} values={funnel.values} />
        </Card>

        <Card $d={8} $p="24px" style={{ maxHeight: 440, overflowY: "auto" }}>
          <Heading $size="15px" $mb="18px">
            Insights & Alerts
          </Heading>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {insights.map((ins, i) => {
              const s = insStyles[ins.type] || insStyles.info;
              return (
                <div
                  key={i}
                  style={{
                    padding: "11px 14px",
                    borderRadius: 10,
                    background: s.bg,
                    borderLeft: `3px solid ${s.bdr}`,
                    fontSize: 12.5,
                    color: C.inkSoft,
                    lineHeight: 1.55
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      background: s.bdr,
                      color: "#fff",
                      fontSize: 10,
                      fontWeight: 700,
                      marginRight: 8,
                      verticalAlign: "middle"
                    }}
                  >
                    {s.ico}
                  </span>
                  {ins.text}
                </div>
              );
            })}
          </div>
        </Card>
      </Grid>

      {/* ── Access + Affordability ──────────────────────────────────── */}
      <Grid $cols="1fr 1fr" $md="1fr" $mb={28}>
        <Card $d={9} $p="28px">
          <Heading $size="16px" $mb="24px" $color={C.ink}>
            Access
          </Heading>
          <div style={{ marginBottom: 36 }}>
            <Heading $size="13px" $mb="12px">
              PA Approval Rate Over Time
            </Heading>
            <LineChart
              sets={[access.paApprovalTrend.data]}
              labels={access.paApprovalTrend.labels}
              colors={[C.emerald]}
              h={130}
              dots={false}
            />
          </div>
          <Grid $cols="1fr 1fr" $gap={24} $mb={0}>
            <div>
              <Heading $size="13px" $mb="12px">
                Time to PA (days)
              </Heading>
              <LineChart
                sets={[access.timeToPA.data]}
                labels={access.timeToPA.labels}
                colors={[C.amber]}
                h={140}
                dots={false}
                area={false}
              />
            </div>
            <div>
              <Heading $size="13px" $mb="12px">
                Coverage Mix
              </Heading>
              <Donut segments={access.coverageMix} size={140} centerLabel="patients" />
            </div>
          </Grid>
        </Card>

        <Card $d={10} $p="28px">
          <Heading $size="16px" $mb="24px" $color={C.ink}>
            Affordability
          </Heading>
          <div style={{ marginBottom: 28 }}>
            <Heading $size="13px" $mb="12px">
              Financial Assistance Mix
            </Heading>
            <StackedBar
              data={[
                affordability.assistanceMix.copay,
                affordability.assistanceMix.pap,
                affordability.assistanceMix.none
              ]}
              labels={affordability.assistanceMix.labels}
              colors={[C.accent, C.violet, C.inkGhost]}
              legendLabels={["Copay", "PAP", "None"]}
              h={130}
            />
          </div>
          <Grid $cols="1fr 1fr" $gap={20} $mb={0}>
            <div>
              <Heading $size="13px" $mb="12px">
                Time to Assistance
              </Heading>
              <LineChart
                sets={[affordability.timeToAssistance.data]}
                labels={affordability.timeToAssistance.labels}
                colors={[C.accent]}
                h={100}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <Heading $size="13px" $mb="16px">
                Copay Enrollment
              </Heading>
              <Gauge value={affordability.copayEnrollRate} label="" color={C.accent} />
            </div>
          </Grid>
        </Card>
      </Grid>

      {/* ── Adherence & Persistence ─────────────────────────────────── */}
      <Grid $cols="1fr 1fr 1fr" $md="1fr 1fr" $sm="1fr" $mb={28}>
        <Card $d={11}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <Heading $size="14px">Days on Therapy</Heading>
            <Badge $color={C.accent} $bg={C.accentGlow}>
              Avg {adherence.dotTrend.data[adherence.dotTrend.data.length - 1]}d
            </Badge>
          </div>
          <LineChart sets={[adherence.dotTrend.data]} labels={adherence.dotTrend.labels} colors={[C.accent]} h={150} />
        </Card>
        <Card $d={12}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <Heading $size="14px">Cycles / Refill per Patient</Heading>
          </div>
          <BarChart
            data={adherence.cyclesDistribution.data}
            labels={adherence.cyclesDistribution.labels}
            color={C.sky}
            h={150}
          />
        </Card>
        <Card $d={13}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <Heading $size="14px">Patient Retention</Heading>
          </div>
          <Survival data={adherence.survivalCurve.data} labels={adherence.survivalCurve.labels} h={150} />
        </Card>
      </Grid>

      {/* ── Engagement ──────────────────────────────────────────────── */}
      <Grid $cols="1fr 1fr 1fr" $md="1fr 1fr" $sm="1fr" $mb={0}>
        <Card $d={14}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <Heading $size="14px">DAU vs MAU</Heading>
            <Tag>Trend</Tag>
          </div>
          <LineChart
            sets={[engagement.dauMau.dau, engagement.dauMau.mau.map((v) => v / 5)]}
            labels={engagement.dauMau.labels}
            colors={[C.accent, C.inkGhost]}
            h={150}
            dots={false}
          />
          <div style={{ display: "flex", gap: 16, marginTop: 14 }}>
            <LegendItem $c={C.accent}>DAU</LegendItem>
            <LegendItem $c={C.inkGhost}>MAU (scaled)</LegendItem>
          </div>
        </Card>
        <Card
          $d={15}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center"
          }}
        >
          <Heading $size="14px" $mb="24px" style={{ alignSelf: "flex-start" }}>
            Engagement Rate
          </Heading>
          <Gauge value={engagement.engagementRate} label="of patients interacting" color={C.accent} size={140} />
        </Card>
        <Card $d={16}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <Heading $size="14px">Content Performance</Heading>
            <Tag>Interactions</Tag>
          </div>
          <BarChart
            data={engagement.contentPerformance.data}
            labels={engagement.contentPerformance.labels}
            color={C.emerald}
            h={150}
            horizontal
          />
        </Card>
      </Grid>
    </Page>
  );
};

export { AnalyticsDashboard };
